import React, { useState, useEffect, useRef } from 'react';
import { Gamepad2, X, Plus, Users, Heart, AlertTriangle, Sparkles, Dice1, RefreshCw, Eye, EyeOff, Shield, BookOpen, Copy } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { doc, updateDoc, onSnapshot, getDoc, setDoc, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { ref, set, onValue, onDisconnect } from 'firebase/database';
import { db, rtdb } from '../../firebase/config';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import StickyVitalsBar from '../character-sheet/StickyVitalsBar';
import SecretDCQueue from './SecretDCQueue';
import ActiveSessionEditor from './ActiveSessionEditor';
import CampaignFrameMechanicsPanel from './CampaignFrameMechanicsPanel';
import GMFearCounter from './GMFearCounter';
import { calculateEvasion, calculateArmorScore, calculateDamageThresholds, calculateProficiency } from '../../utils/characterCalculations';
import { useDaggerheartData } from '../../context/DataContext';
import { getArmorInfo, getWeaponInfo } from '../../utils/equipmentHelpers';
import { refreshTokens } from '../../services/SimpleTokenService';
import { getSpellcastTrait } from '../../services/characterMigrationService';

const GMDashboard = ({ campaign, characters = [] }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const gameData = useDaggerheartData();

  // Get campaignId from campaign prop, not from useCampaign context
  const campaignId = campaign?.id;

  // Ref for ActiveSessionEditor to force save
  const sessionEditorRef = useRef(null);

  // GM Screen deployment state
  const [isScreenDeployed, setIsScreenDeployed] = useState(false);
  const [gmModeActive, setGmModeActive] = useState(campaign?.gmMode?.enabled || false);
  const [dcQueue, setDcQueue] = useState([]);
  const [activePlayers, setActivePlayers] = useState([]);
  const [currentRolls, setCurrentRolls] = useState({});
  const [liveCharacterData, setLiveCharacterData] = useState({}); // Real-time character vitals
  const [gmModeActiveSince, setGmModeActiveSince] = useState(null);

  // Helper function to prepare character data for StickyVitalsBar
  const prepareCharacterForVitalsBar = (character) => {
    // Normalize field names (currentHP -> hp, etc.)
    const normalizedChar = {
      ...character,
      hp: character.hp || character.currentHP || 0,
      maxHp: character.maxHp || character.maxHP || 8,
      stress: character.stress || character.currentStress || 0,
      maxStress: character.maxStress || character.maxStress || 8,
      hope: character.hope || character.currentHope || 0,
      traits: character.traits || {},
      conditions: character.conditions || [],
      scars: character.scars || [],
      loadout: character.loadout || [],
      armorMarks: character.armorMarks || []
    };

    // Wait for gameData to load before calculating equipment stats
    if (!gameData || !gameData.equipmentDatabase || !gameData.campaignFrames) {
      return {
        character: normalizedChar,
        evasion: character.evasion || 10,
        armorScore: character.armorScore || 0,
        thresholds: { minor: 3, major: 7, severe: 13 },
        currentProficiency: character.proficiency || 1,
        spellcastTrait: null
      };
    }

    // Get equipment info for calculations using helper functions
    const armorId = character.equipped?.armor || character.armor;
    const secondaryWeaponId = character.equipped?.secondaryWeapon || character.secondaryWeapon;
    const armorInfo = getArmorInfo(armorId, gameData, character);
    const secondaryWeaponInfo = getWeaponInfo(secondaryWeaponId, gameData, character);
    const classInfo = gameData.classes?.[character.class];

    // Calculate combat stats (with safe fallbacks)
    const evasion = armorInfo ? calculateEvasion(normalizedChar, armorInfo) : (character.evasion || 10);
    const armorScore = armorInfo ? calculateArmorScore(armorInfo, secondaryWeaponInfo, normalizedChar) : (character.armorScore || 0);
    const thresholds = armorInfo ? calculateDamageThresholds(armorInfo, character.level || 1, normalizedChar) : { minor: 3, major: 7, severe: 13 };
    const currentProficiency = classInfo ? calculateProficiency(normalizedChar, classInfo) : (character.proficiency || 1);
    const spellcastTrait = classInfo?.spellcastTrait || null;

    return {
      character: normalizedChar,
      evasion,
      armorScore,
      thresholds,
      currentProficiency,
      spellcastTrait
    };
  };

  // Initialize GM Mode from campaign settings
  useEffect(() => {
    if (campaign?.gmMode) {
      setGmModeActive(campaign.gmMode.enabled);
    }
  }, [campaign]);

  // Set up real-time listeners for GM Mode
  useEffect(() => {
    if (!campaignId || !user) {
      return;
    }

    // Listen to GM Mode state in RTDB
    const gmModeRef = ref(rtdb, `campaigns/${campaignId}/gmMode`);
    const unsubscribeGmMode = onValue(gmModeRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        setGmModeActive(data.active || false);
        setDcQueue(Object.values(data.dcQueue || {}).filter(dc => !dc.used));
        setCurrentRolls(data.currentRolls || {});
      }
    });

    // Listen to active players
    const playersRef = ref(rtdb, `campaigns/${campaignId}/activePlayers`);
    const unsubscribePlayers = onValue(playersRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const playersList = Object.entries(data).map(([uid, playerData]) => ({
          uid,
          ...playerData
        }));
        setActivePlayers(playersList);
      } else {
        setActivePlayers([]);
      }
    });

    // Set up GM online presence
    const gmPresenceRef = ref(rtdb, `campaigns/${campaignId}/gmMode/gmOnline`);
    set(gmPresenceRef, true);
    onDisconnect(gmPresenceRef).set(false);

    return () => {
      unsubscribeGmMode();
      unsubscribePlayers();
      set(gmPresenceRef, false);
    };
  }, [campaignId, user]);

  // Set up real-time Firestore listeners for character vitals
  useEffect(() => {
    if (!characters || characters.length === 0) {
      return;
    }

    // Create array to hold all unsubscribe functions
    const unsubscribers = [];

    // Set up a listener for each character
    characters.forEach((character) => {
      if (!character.id) return;

      const characterRef = doc(db, 'characters', character.id);
      const unsubscribe = onSnapshot(
        characterRef,
        (snapshot) => {
          if (snapshot.exists()) {
            const updatedData = {
              id: snapshot.id,
              ...snapshot.data()
            };

            // Update the live character data for this specific character
            setLiveCharacterData((prev) => ({
              ...prev,
              [character.id]: updatedData
            }));
          }
        },
        (error) => {
          console.error(`Error listening to character ${character.id}:`, error);
        }
      );

      unsubscribers.push(unsubscribe);
    });

    // Cleanup: Unsubscribe from all listeners when component unmounts or characters change
    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  }, [characters.map(c => c.id).join(',')]); // Only re-run if character IDs change

  // Deploy GM Screen
  const handleDeployScreen = async () => {
    try {
      setIsScreenDeployed(true);

      // Automatically activate GM Mode when screen is deployed
      const gmModeRef = ref(rtdb, `campaigns/${campaignId}/gmMode`);
      await set(gmModeRef, {
        active: true,
        gmUserId: user.uid,
        gmOnline: true,
        activeSince: Date.now(),
        dcQueue: dcQueue.reduce((acc, dc, index) => {
          acc[`dc${index}`] = dc;
          return acc;
        }, {}),
        currentRolls: {}
      });

      // Update Firestore for persistence
      const campaignRef = doc(db, 'campaigns', campaignId);
      await updateDoc(campaignRef, {
        'gmMode.enabled': true,
        'gmMode.lastActivated': new Date()
      });

      // Reset GM Fear pool for new session
      const gmFearRef = ref(rtdb, `campaigns/${campaignId}/gameState/gmFear`);
      await set(gmFearRef, { current: 0, lastUpdated: Date.now() });

      // Refresh session-based abilities for all party characters
      let refreshedCount = 0;
      for (const character of characters) {
        if (!character.id) continue;

        try {
          // Get spellcast trait for this character (needed for proper token refresh)
          const spellcastTrait = getSpellcastTrait(character, gameData);
          const refreshedCharacter = refreshTokens(character, 'session', spellcastTrait);

          // Update character in Firestore
          const characterRef = doc(db, 'characters', character.id);
          await updateDoc(characterRef, {
            cardResources: refreshedCharacter.cardResources || {},
            resources: refreshedCharacter.resources || {},
            // Reset any session-based flags
            perfectRecallUsedThisSession: false,
            // Clear Seraph Prayer Dice (SRD: clear at session end)
            prayerDice: refreshedCharacter.prayerDice || [],
            // Clear Sorcerer Channel Raw Power bonus
            channelRawPowerDamageBonus: 0
          });
          refreshedCount++;
        } catch (charError) {
          console.error(`Error refreshing character ${character.name}:`, charError);
        }
      }

      setGmModeActive(true);

      if (refreshedCount > 0) {
        toast.success(`GM Screen Deployed - ${refreshedCount} character${refreshedCount > 1 ? 's' : ''} refreshed!`);
      } else {
        toast.success('GM Screen Deployed');
      }
    } catch (error) {
      console.error('Error deploying GM Screen:', error);
      toast.error('Failed to deploy GM Screen');
    }
  };

  // Listen to GM Mode active status and track activeSince
  useEffect(() => {
    if (!campaignId) return;

    const gmModeRef = ref(rtdb, `campaigns/${campaignId}/gmMode`);
    const unsubscribe = onValue(gmModeRef, (snapshot) => {
      const data = snapshot.val();
      if (data?.active && data?.activeSince) {
        setGmModeActiveSince(data.activeSince);
      } else {
        setGmModeActiveSince(null);
      }
    });

    return () => unsubscribe();
  }, [campaignId]);

  // Lower GM Screen (end session) - Auto-archive with force save
  const handleLowerScreen = async () => {
    try {
      // Force save current notes (override 30s timer)
      let content = null;
      if (sessionEditorRef.current) {
        content = await sessionEditorRef.current.forceSave();
      }

      if (!content || !content.trim()) {
        toast.error('No session notes to archive');
        return;
      }

      // Calculate session stats
      const sessionNumber = campaign?.sessionCount || 1;
      const sessionDuration = gmModeActiveSince
        ? Math.round((Date.now() - gmModeActiveSince) / 1000 / 60) // minutes
        : 0;

      // Calculate word count (simple)
      const textOnly = content.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
      const wordCount = textOnly.split(/\s+/).filter(w => w.length > 0).length;

      // Generate auto title
      const date = new Date().toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
      const title = `Session ${sessionNumber} - ${date}`;

      // Save to archivedNotes subcollection
      const archiveRef = collection(db, 'campaigns', campaignId, 'archivedNotes');
      await addDoc(archiveRef, {
        title,
        content,
        sessionNumber,
        duration: sessionDuration,
        wordCount,
        archivedAt: serverTimestamp(),
        archivedBy: user.displayName || user.email
      });

      // Clear current session draft
      const draftRef = doc(db, 'campaigns', campaignId, 'currentSessionDraft', 'draft');
      await setDoc(draftRef, {
        content: '',
        lastSaved: new Date(),
        updatedBy: user.uid
      });

      // Increment session count in campaign
      const campaignRef = doc(db, 'campaigns', campaignId);
      await updateDoc(campaignRef, {
        sessionCount: sessionNumber + 1,
        lastSessionDate: new Date(),
        'gmMode.enabled': false
      });

      // Deactivate GM Mode in RTDB
      const gmModeRef = ref(rtdb, `campaigns/${campaignId}/gmMode`);
      await set(gmModeRef, {
        active: false,
        gmUserId: user.uid,
        gmOnline: true,
        activeSince: null,
        dcQueue: {},
        currentRolls: {}
      });

      // Reset GM Fear pool
      const gmFearRef = ref(rtdb, `campaigns/${campaignId}/gameState/gmFear`);
      await set(gmFearRef, { current: 0, lastUpdated: Date.now() });

      // Update local state
      setIsScreenDeployed(false);
      setDcQueue([]);
      setGmModeActive(false);
      setGmModeActiveSince(null);

      toast.success(`Session ${sessionNumber} archived!`);
    } catch (error) {
      console.error('Error archiving session:', error);
      toast.error('Failed to archive session');
    }
  };

  // Quick add DC to queue (instant add)
  const handleQuickAddDC = (value) => {
    if (dcQueue.length >= 5) {
      toast.error('DC Queue is full (max 5)');
      return;
    }

    const dc = {
      id: `dc_${Date.now()}`,
      value,
      description: '',
      used: false,
      createdAt: Date.now()
    };

    const newQueue = [...dcQueue, dc];
    setDcQueue(newQueue);

    // Update RTDB
    const queueRef = ref(rtdb, `campaigns/${campaignId}/gmMode/dcQueue`);
    const queueObject = newQueue.reduce((acc, item, index) => {
      acc[`dc${index}`] = item;
      return acc;
    }, {});

    set(queueRef, queueObject);
    toast.success(`DC ${value} added to queue`);
  };

  // Update DC description
  const handleUpdateDCDescription = (dcId, newDescription) => {
    const newQueue = dcQueue.map(dc =>
      dc.id === dcId ? { ...dc, description: newDescription } : dc
    );
    setDcQueue(newQueue);

    // Update RTDB
    const queueRef = ref(rtdb, `campaigns/${campaignId}/gmMode/dcQueue`);
    const queueObject = newQueue.reduce((acc, item, index) => {
      acc[`dc${index}`] = item;
      return acc;
    }, {});

    set(queueRef, queueObject);
  };

  // Remove DC from queue
  const handleRemoveDC = (dcId) => {
    const newQueue = dcQueue.filter(dc => dc.id !== dcId);
    setDcQueue(newQueue);

    // Update RTDB
    const queueRef = ref(rtdb, `campaigns/${campaignId}/gmMode/dcQueue`);
    set(queueRef, newQueue.reduce((acc, item, index) => {
      acc[`dc${index}`] = item;
      return acc;
    }, {}));
  };

  // Reorder DC queue (drag-and-drop)
  const handleReorderDCQueue = (newQueue) => {
    setDcQueue(newQueue);

    // Update RTDB with new order
    const queueRef = ref(rtdb, `campaigns/${campaignId}/gmMode/dcQueue`);
    const queueObject = newQueue.reduce((acc, item, index) => {
      acc[`dc${index}`] = item;
      return acc;
    }, {});

    set(queueRef, queueObject);
  };


  // Get character vitals for party overview
  const getCharacterVitals = (characterId) => {
    const character = characters.find(c => c.id === characterId);
    if (!character) return null;

    return {
      name: character.name,
      class: character.class,
      hp: character.currentHP || 0,
      maxHp: character.maxHP || 0,
      stress: character.currentStress || 0,
      hope: character.currentHope || 0,
      conditions: character.conditions || {}
    };
  };

  return (
    <div className="p-4 lg:p-8">
      {!isScreenDeployed ? (
        /* PRE-DEPLOYMENT VIEW: Deploy Button */
        <div className="mb-6">
          <div className="bg-gradient-to-br from-gray-900 to-black p-8 rounded-lg shadow-2xl border border-red-700 text-center">
            <BookOpen className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-red-400 font-serif mb-2">GM Screen</h2>
            <p className="text-sm text-gray-400 mb-6">Deploy your command center to manage the session</p>
            <button
              onClick={handleDeployScreen}
              className="inline-flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-red-600 to-red-700 text-white font-semibold rounded-lg shadow-lg hover:from-red-500 hover:to-red-600 transition-all focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:ring-offset-gray-900"
            >
              <BookOpen className="w-5 h-5" />
              Deploy GM Screen
            </button>
          </div>
        </div>
      ) : (
        /* DEPLOYED VIEW: 3-Column Trifold Layout */
        <>
          {/* 3-Column GM Screen */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            {/* LEFT PANEL: Secret DC Queue */}
            <SecretDCQueue
              dcQueue={dcQueue}
              onQuickAdd={handleQuickAddDC}
              onUpdateDescription={handleUpdateDCDescription}
              onRemove={handleRemoveDC}
              onReorder={handleReorderDCQueue}
              maxQueue={5}
            />

            {/* MIDDLE PANEL: Active Session Notes Editor */}
            <div className="bg-gradient-to-br from-gray-900 to-black p-4 rounded-lg shadow-2xl border border-red-700 flex flex-col max-h-[700px]">
              <h3 className="text-lg font-bold text-red-400 font-serif mb-4 flex items-center gap-2 flex-shrink-0">
                <Sparkles className="w-5 h-5 text-red-400" />
                Session Notes
              </h3>
              <div className="flex-1 min-h-0 overflow-y-auto">
                <ActiveSessionEditor
                  ref={sessionEditorRef}
                  campaignId={campaignId}
                  userId={user?.uid}
                />
              </div>
            </div>

            {/* RIGHT PANEL: Fear + Encounters + Campaign Mechanics */}
            <div className="flex flex-col gap-6">
              {/* GM Fear Pool */}
              <GMFearCounter characters={characters} />

              {/* Encounter Manager Placeholder */}
              <div className="bg-gradient-to-br from-gray-900 to-black p-4 rounded-lg shadow-2xl border border-red-700 flex flex-col">
                <h3 className="text-lg font-bold text-red-400 font-serif mb-4 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-red-400" />
                  Encounters
                </h3>
                <div className="text-center py-8 text-gray-500 border border-gray-700 rounded-lg">
                  <Shield className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">Monster & NPC Manager</p>
                  <p className="text-xs mt-1">Coming in Phase 2</p>
                </div>
              </div>

              {/* Campaign Frame Mechanics */}
              <div className="bg-gradient-to-br from-gray-900 to-black p-4 rounded-lg shadow-2xl border border-red-700 flex flex-col">
                <h3 className="text-lg font-bold text-red-400 font-serif mb-4 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-red-400" />
                  Campaign Mechanics
                </h3>
                <CampaignFrameMechanicsPanel campaign={campaign} characters={characters} />
              </div>
            </div>
          </div>

          {/* Lower Screen Button */}
          <div className="mb-6">
            <button
              onClick={handleLowerScreen}
              className="w-full px-6 py-3 bg-gradient-to-r from-gray-700 to-gray-800 text-gray-300 font-semibold rounded-lg shadow-lg hover:from-gray-600 hover:to-gray-700 transition-all focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 focus:ring-offset-gray-900 flex items-center justify-center gap-2"
            >
              <BookOpen className="w-5 h-5" />
              Lower the Screen
            </button>
          </div>
        </>
      )}

      {/* Party Roster Panel - Matching Session Notes Aesthetic */}
      <div className="bg-gradient-to-br from-gray-900 to-black p-4 rounded-lg shadow-2xl border border-red-700">
        <h3 className="text-lg font-bold text-red-400 font-serif mb-4 flex items-center gap-2">
          <Users className="w-5 h-5 text-red-400" />
          Party Roster
          <span className="text-xs text-gray-500">({characters.length} total)</span>
        </h3>

        {/* Quick Party Vitals Summary */}
        {characters.length > 0 && (
          <div className="mb-4 p-3 bg-gray-800/50 rounded-lg border border-gray-700">
            <div className="flex flex-wrap gap-3 text-xs">
              {characters.map(char => {
                const currentCharData = liveCharacterData[char.id] || char;
                const hp = currentCharData.hp || currentCharData.currentHP || 0;
                const maxHp = currentCharData.maxHp || currentCharData.maxHP || 8;
                const stress = currentCharData.stress || currentCharData.currentStress || 0;
                const maxStress = currentCharData.maxStress || 8;
                const hope = currentCharData.hope || currentCharData.currentHope || 0;
                const hpPercent = maxHp > 0 ? (hp / maxHp) * 100 : 0;
                const isActive = activePlayers.some(p => p.characterId === char.id);

                return (
                  <div
                    key={char.id}
                    className={`flex items-center gap-2 px-2 py-1 rounded ${
                      isActive ? 'bg-gray-700' : 'bg-gray-800 opacity-60'
                    }`}
                    title={`${char.name}: ${hp}/${maxHp} HP, ${stress}/${maxStress} Stress, ${hope} Hope`}
                  >
                    <span className={`w-2 h-2 rounded-full ${isActive ? 'bg-green-400' : 'bg-gray-500'}`} />
                    <span className="font-medium text-gray-300">{char.name?.split(' ')[0]}</span>
                    <span className={`font-bold ${
                      hpPercent <= 25 ? 'text-red-400' : hpPercent <= 50 ? 'text-yellow-400' : 'text-green-400'
                    }`}>
                      ❤️{hp}
                    </span>
                    <span className={`${stress >= maxStress - 1 ? 'text-orange-400' : 'text-gray-400'}`}>
                      💫{stress}
                    </span>
                    <span className="text-yellow-400">✨{hope}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {characters.length > 0 ? (
          <div className="space-y-4">
            {/* Active Players Section */}
            {(() => {
              const activeCharacters = characters.filter(char =>
                activePlayers.some(player => player.characterId === char.id)
              );

              return activeCharacters.length > 0 && (
                <>
                  <div className="mb-2">
                    <h4 className="text-xs font-semibold text-green-400 uppercase tracking-wider mb-2">
                      Active ({activeCharacters.length})
                    </h4>
                  </div>
                  <div className="space-y-2">
                    {activeCharacters.map(character => {
                      const player = activePlayers.find(p => p.characterId === character.id);
                      const isRolling = currentRolls[player?.uid];
                      // Use live character data if available, otherwise use static data
                      const currentCharData = liveCharacterData[character.id] || character;
                      const vitalsData = prepareCharacterForVitalsBar(currentCharData);

                      if (!vitalsData) return null;

                      return (
                        <div
                          key={character.id}
                          onClick={() => navigate(`/character/${character.id}`)}
                          className="relative bg-[#282828] border border-gray-600 rounded-lg hover:bg-[#2C2C2C] hover:border-blue-500 transition-all cursor-pointer overflow-hidden"
                        >
                          {/* Character Name & Online Indicator */}
                          <div className="px-3 pt-3 pb-2 flex items-center justify-between">
                            <div className="flex items-center gap-2 flex-wrap">
                              <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                                isRolling ? 'bg-yellow-400 animate-pulse' : 'bg-green-400'
                              }`} />
                              <span className="text-sm font-bold text-gray-100">{currentCharData.name}</span>
                              <span className="text-xs text-gray-500">-</span>
                              <span className="text-xs text-gray-400">
                                Level {currentCharData.level || 1} {currentCharData.class?.charAt(0).toUpperCase() + currentCharData.class?.slice(1)}
                                {currentCharData.subclass && `: ${currentCharData.subclass.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}`}
                              </span>
                              {(currentCharData.ancestry || currentCharData.community) && (
                                <>
                                  <span className="text-xs text-gray-500">-</span>
                                  <span className="text-xs text-gray-400">
                                    {(() => {
                                      // Handle ancestry (string, object, or array for mixed ancestry)
                                      let ancestryStr = '';
                                      if (typeof currentCharData.ancestry === 'string') {
                                        ancestryStr = currentCharData.ancestry;
                                      } else if (Array.isArray(currentCharData.ancestry)) {
                                        // Mixed ancestry - join all with /
                                        ancestryStr = currentCharData.ancestry.map(a =>
                                          a.charAt(0).toUpperCase() + a.slice(1)
                                        ).join('/');
                                      } else if (currentCharData.ancestry?.name) {
                                        ancestryStr = currentCharData.ancestry.name;
                                      }

                                      // Handle community (string, object, or array)
                                      let communityStr = '';
                                      if (typeof currentCharData.community === 'string') {
                                        communityStr = currentCharData.community;
                                      } else if (Array.isArray(currentCharData.community)) {
                                        communityStr = currentCharData.community.map(c =>
                                          c.charAt(0).toUpperCase() + c.slice(1)
                                        ).join('/');
                                      } else if (currentCharData.community?.name) {
                                        communityStr = currentCharData.community.name;
                                      }

                                      // Build final display
                                      const parts = [];
                                      if (ancestryStr) parts.push(ancestryStr.charAt(0).toUpperCase() + ancestryStr.slice(1));
                                      if (communityStr) parts.push(communityStr.charAt(0).toUpperCase() + communityStr.slice(1));
                                      return parts.join(' - ');
                                    })()}
                                  </span>
                                </>
                              )}
                            </div>
                            {isRolling && (
                              <span className="flex items-center gap-1 text-xs text-yellow-400 flex-shrink-0">
                                <RefreshCw className="w-3 h-3 animate-spin" />
                                Rolling...
                              </span>
                            )}
                          </div>

                          {/* StickyVitalsBar (modified to be inline, not fixed) */}
                          <div className="[&>div]:!static [&>div]:!top-auto [&>div]:!left-auto [&>div]:!right-auto [&>div]:!z-auto [&>div]:!rounded-none [&>div]:!overflow-visible [&>div]:bg-transparent [&>div]:!w-auto [&>div]:!max-w-none [&>div]:!p-0">
                            <StickyVitalsBar
                              character={vitalsData.character}
                              companion={null}
                              updateVitals={() => {}} // No-op (read-only for GM)
                              toggleCondition={() => {}} // No-op (read-only for GM)
                              hpDamageMode="normal"
                              toggleHPDamageMode={() => {}} // No-op (read-only for GM)
                              currentProficiency={vitalsData.currentProficiency}
                              evasion={vitalsData.evasion}
                              armorScore={vitalsData.armorScore}
                              thresholds={vitalsData.thresholds}
                              markArmor={() => {}} // No-op (read-only for GM)
                              spellcastTrait={vitalsData.spellcastTrait}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Separator between Active and Inactive */}
                  {characters.length > activeCharacters.length && (
                    <div className="h-px bg-gray-700" />
                  )}
                </>
              );
            })()}

            {/* Inactive Players Section */}
            {(() => {
              const inactiveCharacters = characters.filter(char =>
                !activePlayers.some(player => player.characterId === char.id)
              );

              return inactiveCharacters.length > 0 && (
                <>
                  <div className="mb-2">
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                      Inactive ({inactiveCharacters.length})
                    </h4>
                  </div>
                  <div className="space-y-2">
                  {inactiveCharacters.map(character => {
                    // Use live character data if available, otherwise use static data
                    const currentCharData = liveCharacterData[character.id] || character;
                    const vitalsData = prepareCharacterForVitalsBar(currentCharData);

                    if (!vitalsData) return null;

                    return (
                      <div
                        key={character.id}
                        onClick={() => navigate(`/character/${character.id}`)}
                        className="relative bg-[#1E1E1E] border border-gray-700 rounded-lg hover:bg-[#232323] hover:border-gray-600 transition-all cursor-pointer overflow-hidden opacity-60 hover:opacity-100"
                      >
                        {/* Character Name & Offline Indicator */}
                        <div className="px-3 pt-3 pb-2 flex items-center justify-between">
                          <div className="flex items-center gap-2 flex-wrap">
                            <div className="w-2 h-2 rounded-full bg-gray-600 flex-shrink-0" />
                            <span className="text-sm font-bold text-gray-300">{currentCharData.name}</span>
                            <span className="text-xs text-gray-600">-</span>
                            <span className="text-xs text-gray-500">
                              Level {currentCharData.level || 1} {currentCharData.class?.charAt(0).toUpperCase() + currentCharData.class?.slice(1)}
                              {currentCharData.subclass && `: ${currentCharData.subclass.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}`}
                            </span>
                            {(currentCharData.ancestry || currentCharData.community) && (
                              <>
                                <span className="text-xs text-gray-600">-</span>
                                <span className="text-xs text-gray-500">
                                  {(() => {
                                    // Handle ancestry (string, object, or array for mixed ancestry)
                                    let ancestryStr = '';
                                    if (typeof currentCharData.ancestry === 'string') {
                                      ancestryStr = currentCharData.ancestry;
                                    } else if (Array.isArray(currentCharData.ancestry)) {
                                      // Mixed ancestry - join all with /
                                      ancestryStr = currentCharData.ancestry.map(a =>
                                        a.charAt(0).toUpperCase() + a.slice(1)
                                      ).join('/');
                                    } else if (currentCharData.ancestry?.name) {
                                      ancestryStr = currentCharData.ancestry.name;
                                    }

                                    // Handle community (string, object, or array)
                                    let communityStr = '';
                                    if (typeof currentCharData.community === 'string') {
                                      communityStr = currentCharData.community;
                                    } else if (Array.isArray(currentCharData.community)) {
                                      communityStr = currentCharData.community.map(c =>
                                        c.charAt(0).toUpperCase() + c.slice(1)
                                      ).join('/');
                                    } else if (currentCharData.community?.name) {
                                      communityStr = currentCharData.community.name;
                                    }

                                    // Build final display
                                    const parts = [];
                                    if (ancestryStr) parts.push(ancestryStr.charAt(0).toUpperCase() + ancestryStr.slice(1));
                                    if (communityStr) parts.push(communityStr.charAt(0).toUpperCase() + communityStr.slice(1));
                                    return parts.join(' - ');
                                  })()}
                                </span>
                              </>
                            )}
                          </div>
                          <span className="text-xs text-gray-600 flex-shrink-0">Offline</span>
                        </div>

                        {/* StickyVitalsBar (modified to be inline, not fixed) */}
                        <div className="[&>div]:!static [&>div]:!top-auto [&>div]:!left-auto [&>div]:!right-auto [&>div]:!z-auto [&>div]:!rounded-none [&>div]:!overflow-visible [&>div]:bg-transparent [&>div]:!w-auto [&>div]:!max-w-none [&>div]:!p-0">
                          <StickyVitalsBar
                            character={vitalsData.character}
                            companion={null}
                            updateVitals={() => {}} // No-op (read-only for GM)
                            toggleCondition={() => {}} // No-op (read-only for GM)
                            hpDamageMode="normal"
                            toggleHPDamageMode={() => {}} // No-op (read-only for GM)
                            currentProficiency={vitalsData.currentProficiency}
                            evasion={vitalsData.evasion}
                            armorScore={vitalsData.armorScore}
                            thresholds={vitalsData.thresholds}
                            markArmor={() => {}} // No-op (read-only for GM)
                            spellcastTrait={vitalsData.spellcastTrait}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
                </>
              );
            })()}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <Users className="w-12 h-12 mx-auto mb-2 opacity-30" />
            <p>No party members yet</p>
            <p className="text-xs text-gray-600 mt-1">Share your invite link so players can join</p>
            <button
              onClick={() => {
                const link = `${window.location.origin}/join-campaign/${campaignId}`;
                navigator.clipboard.writeText(link).then(() => {
                  toast.success('Invite link copied!');
                }).catch(() => {
                  toast.error('Failed to copy link');
                });
              }}
              className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-red-900/40 text-red-300 rounded-md hover:bg-red-900/60 border border-red-700 transition-all duration-200 text-sm font-medium"
            >
              <Copy className="w-4 h-4" />
              Copy Invite Link
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default GMDashboard;